import apiClient from './apiClient';
import configData from '../configure';

/**
 * Service for fetching calendar events for a specific organization and date range.
 */
export const fetchEvents = async ({ orgId, startDate, endDate }) => {
  if (!orgId) throw new Error('Organization ID is required');

  const response = await apiClient.get(
    configData.RESOURCES_EVENTS +
    `orgId=${orgId}` +
    `&eventStartDate=${startDate}` +
    `&eventEndDate=${endDate}` +
    `&pageSize=100`
  );
  
  return response.data;
};

/**
 * Service for fetching mailbox items (Inbox, Sent, etc.)
 */
export const fetchMailboxItems = async ({ userId, orgId, folder = 'INBOX', lastKey = null, size = 5 }) => {
  if (!userId || !orgId) throw new Error('User ID and Org ID are required');

  const response = await apiClient.get(configData.INBOX_MAILBOX, {
    params: { userId, orgId, folder, lastKey, size }
  });
  
  return response.data;
};

/**
 * Service for fetching quick notes for a specific user.
 */
export const fetchUserNotes = async (userId) => {
  if (!userId) throw new Error('User ID is required');
  
  const response = await apiClient.get(
    configData.get_resources_resources_quick_notes_user_pagesize_50(userId)
  );
  
  return response.data;
};

/**
 * Service for creating a new quick note.
 */
export const createNote = async (payload) => {
  const response = await apiClient.post(configData.RESOURCES_QUICK_NOTES, payload);
  return response.data;
};

/**
 * Service for updating an existing quick note.
 */
export const updateNote = async (noteId, payload) => {
  const response = await apiClient.put(
    configData.get_resources_resources_quick_notes(noteId),
    payload
  );
  return response.data;
};

/**
 * Service for deleting a quick note.
 */
export const deleteNote = async (noteId) => {
  const response = await apiClient.delete(
    configData.get_resources_resources_quick_notes(noteId)
  );
  return response.data;
};

/**
 * Service for fetching all classes and their schedules for a faculty member.
 * This aggregates course plans, course details, and class schedules.
 */
export const fetchFacultySchedule = async (userId) => {
  if (!userId || userId === 'unknown') throw new Error('Valid User ID is required');

  // 1. Fetch course plans for this faculty
  const plansRes = await apiClient.get(
    `${configData.COURSE_PLAN}/filter?filterType=faculty&id=${userId}`
  );
  const coursePlans = plansRes.data || [];
  
  if (coursePlans.length === 0) return [];

  const classIds = [...new Set(coursePlans.map(p => p.classId))];

  // 2. Fetch detailed class data (which includes the schedule map)
  const classesPromises = classIds.map(async classId => {
    try {
      const res = await apiClient.get(`${configData.CLASS_BY_ID}/${classId}`);
      const classData = res.data;

      const facultySchedule = {};
      Object.entries(classData.classScheduleMap || {}).forEach(([day, sessions]) => {
        const filtered = sessions.filter(s => s.facultyId === userId);
        if (filtered.length) facultySchedule[day] = filtered;
      });

      return {
        id: classId,
        className: classData.className,
        sectionName: classData.sectionName,
        facultySchedule,
      };
    } catch (err) {
      console.warn(`Failed to fetch class ${classId}`, err);
      return null;
    }
  });

  const results = await Promise.all(classesPromises);
  return results.filter(item => item !== null);
};
